import React, { memo } from 'react'
import dynamic from 'next/dynamic'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Page from 'components/layout/Page'
import get from 'lodash/get'
import { formatFullDate } from 'utils/date'
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
} from 'react-share'
import Text, { TEXT_TYPES } from 'components/designSystem/Text'
import FbIcon from 'components/icons/facebook.svg'
import LnIcon from 'components/icons/linkedin.svg'
import TwIcon from 'components/icons/twitter.svg'
import { FormattedMessage } from 'react-intl'
import { TESTIDS_BLOG as TESTIDS } from 'testids'
import Breadcrumbs from 'apps/VehicleDetail/ClikAuto/Breadcrumbs'
import { Typography } from '@mui/material'
import Image from 'next/legacy/image'
import useBlogDetail from './useBlogDetail'
import sx from './styles'

const PostsDynamic = dynamic(() => import('components/BlogPage/Posts'))

const BlogDetail = () => {
  const {
    head,
    relatedPosts,
    currentUrl,
    content,
    imageTitle,
    imageHeight,
    date,
    title,
    // eslint-disable-next-line camelcase
    source_url,
    post,
    descriptionMeta,
    metaTitle,
    isMobile,
    socialNetworks,
    breadcrumbs,
  } = useBlogDetail()

  const shareIn = (
    <Box
      sx={sx.shareIn}
      my={2}
    >
      <Text type={TEXT_TYPES.body.x2}>
        <FormattedMessage
          id="BLOG_DETAIL_PUBLISHED"
          values={{ date: formatFullDate(date) }}
        />
      </Text>
      <Box sx={sx.toShare}>
        <FacebookShareButton
          url={currentUrl}
          title={metaTitle}
          aria-label="Facebook"
        >
          <FbIcon />
        </FacebookShareButton>
        <LinkedinShareButton
          url={currentUrl}
          title={metaTitle}
          description={descriptionMeta}
          aria-label="LinkedIn"
        >
          <LnIcon />
        </LinkedinShareButton>
        <TwitterShareButton
          url={currentUrl}
          title={metaTitle}
          aria-label="Twitter"
        >
          <TwIcon />
        </TwitterShareButton>
      </Box>
    </Box>
  )

  return (
    <Page>
      {head}
      <Container
        maxWidth="lg"
        sx={{ pt: 7 }}
      >
        <Box mt={2}>
          <Breadcrumbs
            sections={breadcrumbs}
          />
        </Box>
        <Typography
          variant="h1"
          my={1}
        >
          {post && get(title, 'rendered', '')}
        </Typography>
        {shareIn}
      </Container>
      <Box
        sx={{ ...sx.imgContainer, ...{ height: imageHeight } }}
        my={2}
      >
        <Image
          // eslint-disable-next-line camelcase
          src={source_url || ''} // Need default image
          alt={imageTitle}
          title={imageTitle}
          layout="fill"
          objectFit={isMobile ? 'cover' : 'contain'}
          // eslint-disable-next-line camelcase
          blurDataURL={source_url}
          placeholder="blur"
        />
      </Box>
      <Container maxWidth="lg">
        <Box
          sx={sx.data}
          dangerouslySetInnerHTML={{ __html: get(content, 'rendered', '') }}
        />

        {shareIn}

        <Box
          my={3}
          sx={sx.socialNetworks}
        >
          <Text type={TEXT_TYPES.body.x1}>
            <FormattedMessage id="FOLLOW_IN_SOCIAL_NETWORKS" />
          </Text>
          <Box
            m={2}
            sx={sx.socialNetworksItems}
          >
            {socialNetworks.map((item, index) => (
              <Text
                key={index}
                type={TEXT_TYPES.link.text}
                href={item.href}
                target="_blank"
                rel="noreferrer"
                aria-label={item.alt}
              >
                {item.icon}
              </Text>
            ))}
          </Box>
          <Text type={TEXT_TYPES.body.x1}>
            <FormattedMessage id="DO_NOT_MISS_NEWS" />
          </Text>
        </Box>
        <Box sx={sx.relatedPosts}>
          {relatedPosts.length > 0 && (
            <PostsDynamic
              title={<FormattedMessage id="BLOG_RELATED_POST" />}
              testId={TESTIDS.BLOG_RELATED_POSTS}
              posts={relatedPosts}
            />
          )}
        </Box>

      </Container>
    </Page>
  )
}

export default memo(BlogDetail)
